/**
 * DrawsTab — Chad approves pending draw requests; Shannon disburses approved
 * draws and marks them paid in the CRM.
 *
 * NOTE: The CRM no longer pushes bills to QuickBooks; bill creation in QBO,
 * if needed, is handled outside the CRM. No receipt email goes from here: the
 * vendor-payment-receipt function was retired on Oct 20, 2026 (it never
 * delivered one); a draw recorded in Books emails the vendor from Books.
 *
 * Sections:
 *   1. Pending draw requests (Chad-only action area)
 *   2. Approved, awaiting disbursement (Shannon-only action area)
 *   3. Outstanding balances (read-only, all roles)
 *
 * "Record a draw" (finance / admin / sales director): a draw entered directly — no request, approved at
 * once — paid from the company's bank, by the recorder personally, or already owed to the company
 * (commission_draw_record, migration 20261028p_commission_draw_direct).
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 6, §5, §10c #3)
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { fmtUSD } from "../../lib/analytics";
import { notifyOnDrawDecision } from "../../lib/notifyChadOnDrawRequest";
import type { TeamRole } from "../../lib/types";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import RecordDrawModal from "../draws/RecordDrawModal";

interface DrawRow {
  id: string;
  recipient_kind: "team_member" | "vendor";
  team_member_id: string | null;
  vendor_id: string | null;
  requested_amount: number;
  requested_at: string;
  requested_by: string;
  request_reason: string | null;
  approval_status: "pending" | "approved" | "rejected" | "cancelled";
  approved_amount: number | null;
  approval_notes: string | null;
  approved_by: string | null;
  approved_at: string | null;
  paid_at: string | null;
  paid_by: string | null;
  paid_method: string | null;
  paid_reference: string | null;
  notes: string | null;
}

interface RepaymentRow {
  draw_id: string;
  amount: number;
}

function canApproveDraws(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "admin" || role === "sales_director";
}

function canDisburse(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "admin" || role === "finance";
}

function canRecordDraws(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "admin" || role === "finance" || role === "sales_director";
}

export default function DrawsTab() {
  const { role, isSuperUser, member } = useAuth();
  const [draws, setDraws] = useState<DrawRow[] | null>(null);
  const [repayments, setRepayments] = useState<RepaymentRow[]>([]);
  const [members, setMembers] = useState<Record<string, { full_name: string; role: TeamRole }>>({});
  const [vendors, setVendors] = useState<Record<string, { name: string; primary_email: string | null }>>({});
  const [acting, setActing] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [recording, setRecording] = useState(false);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("commission_draws", reload);

  // Per-pending edit state — Chad can edit the approved amount + add note.
  const [edits, setEdits] = useState<Record<string, { amt: string; note: string }>>({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [drawsRes, repaysRes, membersRes, vendorsRes] = await Promise.all([
        supabase.from("commission_draws").select("*").order("requested_at", { ascending: false }),
        supabase.from("commission_draw_repayments").select("draw_id, amount"),
        supabase.from("team_roster").select("id, full_name, role").eq("is_active", true),
        supabase.from("vendors").select("id, name, primary_email"),
      ]);
      if (cancelled) return;
      const err = drawsRes.error ?? repaysRes.error ?? membersRes.error ?? vendorsRes.error;
      if (err) {
        setError(err.message);
        return;
      }
      setDraws((drawsRes.data ?? []) as DrawRow[]);
      setRepayments((repaysRes.data ?? []) as RepaymentRow[]);
      const mMap: Record<string, { full_name: string; role: TeamRole }> = {};
      (membersRes.data ?? []).forEach((m: { id: string; full_name: string; role: TeamRole }) => {
        mMap[m.id] = { full_name: m.full_name, role: m.role };
      });
      setMembers(mMap);
      const vMap: Record<string, { name: string; primary_email: string | null }> = {};
      (vendorsRes.data ?? []).forEach((v: { id: string; name: string; primary_email: string | null }) => {
        vMap[v.id] = { name: v.name, primary_email: v.primary_email };
      });
      setVendors(vMap);

      // Initialize edit buffer for each pending row
      const initEdits: Record<string, { amt: string; note: string }> = {};
      for (const d of (drawsRes.data ?? []) as DrawRow[]) {
        if (d.approval_status === "pending") {
          initEdits[d.id] = { amt: String(d.requested_amount), note: "" };
        }
      }
      setEdits(initEdits);
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Repayment totals per draw (for outstanding balance computation)
  const repaidByDraw = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of repayments) m[r.draw_id] = (m[r.draw_id] || 0) + Number(r.amount);
    return m;
  }, [repayments]);

  function recipientName(d: DrawRow): string {
    if (d.recipient_kind === "team_member" && d.team_member_id) return members[d.team_member_id]?.full_name ?? "—";
    if (d.recipient_kind === "vendor" && d.vendor_id) return vendors[d.vendor_id]?.name ?? "—";
    return "—";
  }
  function recipientRole(d: DrawRow): string {
    if (d.recipient_kind === "vendor") return "Vendor";
    if (d.team_member_id && members[d.team_member_id]) {
      const role = members[d.team_member_id].role;
      return ({ asm: "ASM", team_lead: "TL", roof_specialist: "RS", admin: "Admin" } as Record<string, string>)[role] ?? role;
    }
    return "—";
  }

  async function approveDraw(d: DrawRow) {
    setError(null);
    const e = edits[d.id];
    const amt = parseFloat(e?.amt ?? String(d.requested_amount));
    if (!amt || amt <= 0) {
      setError("Approved amount must be positive.");
      return;
    }
    setActing((s) => new Set(s).add(d.id));
    try {
      // approved_by + actor_id reference team_roster(id), not auth.users(id).
      const actorId = member?.id ?? null;
      const { error: updateErr } = await supabase
        .from("commission_draws")
        .update({
          approval_status: "approved",
          approved_amount: amt,
          approval_notes: e?.note?.trim() || null,
          approved_by: actorId,
          approved_at: new Date().toISOString(),
        })
        .eq("id", d.id);
      if (updateErr) throw updateErr;

      await supabase.from("finance_audit_log").insert({
        actor_id: actorId,
        action: "draw_approved",
        is_financial: true,
        amount_delta: amt,
        before_jsonb: { requested_amount: d.requested_amount, status: "pending" },
        after_jsonb: { approved_amount: amt, draw_id: d.id, recipient_kind: d.recipient_kind, recipient_id: d.team_member_id || d.vendor_id },
        reason: e?.note?.trim() || `Draw approved at ${fmtUSD(amt)}`,
      });

      notifyOnDrawDecision({
        draw_id: d.id,
        recipient_team_member_id: d.team_member_id,
        recipient_kind: d.recipient_kind,
        decision: "approved",
        approved_amount: amt,
        reason: e?.note?.trim() || null,
      });

      reload();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing((s) => { const n = new Set(s); n.delete(d.id); return n; });
    }
  }

  async function rejectDraw(d: DrawRow) {
    setError(null);
    const e = edits[d.id];
    setActing((s) => new Set(s).add(d.id));
    try {
      const actorId = member?.id ?? null;
      const { error: updateErr } = await supabase
        .from("commission_draws")
        .update({
          approval_status: "rejected",
          approval_notes: e?.note?.trim() || null,
          approved_by: actorId,
          approved_at: new Date().toISOString(),
        })
        .eq("id", d.id);
      if (updateErr) throw updateErr;

      await supabase.from("finance_audit_log").insert({
        actor_id: actorId,
        action: "draw_rejected",
        is_financial: false,
        before_jsonb: { requested_amount: d.requested_amount, status: "pending" },
        after_jsonb: { draw_id: d.id, recipient_kind: d.recipient_kind, recipient_id: d.team_member_id || d.vendor_id },
        reason: e?.note?.trim() || "Draw rejected",
      });

      notifyOnDrawDecision({
        draw_id: d.id,
        recipient_team_member_id: d.team_member_id,
        recipient_kind: d.recipient_kind,
        decision: "rejected",
        approved_amount: null,
        reason: e?.note?.trim() || null,
      });

      reload();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing((s) => { const n = new Set(s); n.delete(d.id); return n; });
    }
  }

  async function markPaid(d: DrawRow, method: string, ref: string) {
    setError(null);
    setActing((s) => new Set(s).add(`pay-${d.id}`));
    try {
      const actorId = member?.id ?? null;
      const { error: updateErr } = await supabase
        .from("commission_draws")
        .update({
          paid_at: new Date().toISOString(),
          paid_by: actorId,
          paid_method: method,
          paid_reference: ref || null,
        })
        .eq("id", d.id);
      if (updateErr) throw updateErr;

      await supabase.from("finance_audit_log").insert({
        actor_id: actorId,
        action: "draw_disbursed",
        is_financial: true,
        amount_delta: -Number(d.approved_amount ?? d.requested_amount),
        after_jsonb: { draw_id: d.id, paid_method: method, paid_reference: ref || null, recipient_kind: d.recipient_kind, recipient_id: d.team_member_id || d.vendor_id },
        reason: `Draw disbursed via ${method}${ref ? ` (ref ${ref})` : ""}`,
      });

      reload();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing((s) => { const n = new Set(s); n.delete(`pay-${d.id}`); return n; });
    }
  }

  if (error) return <div className="bg-red-50 text-red-700 p-3 rounded text-sm">{error}</div>;
  if (!draws) return <div className="p-4 text-slate-500 text-sm">Loading draws…</div>;

  const pending  = draws.filter((d) => d.approval_status === "pending");
  const approved = draws.filter((d) => d.approval_status === "approved" && !d.paid_at);
  const paidThisCycle = draws.filter((d) => d.paid_at && new Date(d.paid_at).getTime() > Date.now() - 30 * 86400000);

  // Outstanding balances: per-recipient sum of (paid approved_amount) − (repayments)
  const balances: Array<{ key: string; name: string; role: string; balance: number; kind: "team_member" | "vendor" }> = [];
  const balMap: Record<string, { paid: number; repaid: number; name: string; role: string; kind: "team_member" | "vendor" }> = {};
  for (const d of draws) {
    if (d.approval_status !== "approved" || !d.paid_at) continue;
    const key = (d.recipient_kind === "vendor" ? "v:" : "t:") + (d.vendor_id ?? d.team_member_id ?? "");
    if (!balMap[key]) {
      balMap[key] = {
        paid: 0,
        repaid: 0,
        name: recipientName(d),
        role: recipientRole(d),
        kind: d.recipient_kind,
      };
    }
    balMap[key].paid += Number(d.approved_amount ?? 0);
    balMap[key].repaid += repaidByDraw[d.id] ?? 0;
  }
  for (const [key, v] of Object.entries(balMap)) {
    const balance = v.paid - v.repaid;
    if (balance > 0.01) balances.push({ key, name: v.name, role: v.role, balance, kind: v.kind });
  }
  balances.sort((a, b) => b.balance - a.balance);

  return (
    <div className="space-y-5">
      {canRecordDraws(role, isSuperUser) && (
        <div className="flex justify-end">
          <button
            onClick={() => setRecording(true)}
            className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-orange text-white hover:opacity-90"
          >
            + Record a draw
          </button>
        </div>
      )}
      {recording && (
        <RecordDrawModal members={members} vendors={vendors} onClose={() => setRecording(false)} onRecorded={reload} />
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Pending" value={pending.length} sub={`${fmtUSD(pending.reduce((s, d) => s + Number(d.requested_amount), 0))} total ask`} valueClass="text-amber-600" />
        <SummaryCard label="Awaiting disbursement" value={approved.length} sub={`${fmtUSD(approved.reduce((s, d) => s + Number(d.approved_amount ?? 0), 0))} approved`} valueClass="text-blue-600" />
        <SummaryCard label="Outstanding balances" value={balances.length} sub={`${fmtUSD(balances.reduce((s, b) => s + b.balance, 0))} total`} valueClass="text-purple-600" />
      </div>

      {/* PENDING — Chad's queue */}
      <Section title="Pending draw requests" sub={canApproveDraws(role, isSuperUser) ? "Approve, edit the amount, or reject. Approved draws drop into Shannon's Pay Bills queue." : "Read-only — Chad / Greg approve."}>
        {pending.length === 0 ? (
          <Empty>🎉 No pending draw requests.</Empty>
        ) : (
          pending.map((d) => {
            const e = edits[d.id] ?? { amt: String(d.requested_amount), note: "" };
            const isActing = acting.has(d.id);
            return (
              <div key={d.id} className="bg-white border border-slate-200 rounded-xl p-4 mb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">
                      {recipientName(d)}{" "}
                      <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                        {recipientRole(d)}
                      </span>
                    </div>
                    {d.request_reason && <div className="text-xs text-slate-600 mt-1 italic">"{d.request_reason}"</div>}
                    <div className="text-[11px] text-slate-500 mt-1">requested {timeAgo(d.requested_at)}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-extrabold text-purple-700">{fmtUSD(Number(d.requested_amount))}</div>
                  </div>
                </div>
                {canApproveDraws(role, isSuperUser) && (
                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                    <label className="text-[11px] text-slate-500">Approve at:</label>
                    <input
                      type="number"
                      value={e.amt}
                      onChange={(ev) => setEdits((p) => ({ ...p, [d.id]: { ...e, amt: ev.target.value } }))}
                      className="w-24 text-sm border border-slate-300 rounded px-2 py-1 text-right"
                    />
                    <button
                      onClick={() => approveDraw(d)}
                      disabled={isActing}
                      className="px-3 py-1.5 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {isActing ? "…" : "Approve"}
                    </button>
                    <button
                      onClick={() => rejectDraw(d)}
                      disabled={isActing}
                      className="px-3 py-1.5 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Reject
                    </button>
                    <input
                      type="text"
                      placeholder="Note (optional)"
                      value={e.note}
                      onChange={(ev) => setEdits((p) => ({ ...p, [d.id]: { ...e, note: ev.target.value } }))}
                      className="flex-1 min-w-[150px] text-xs border border-slate-300 rounded px-2 py-1"
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </Section>

      {/* APPROVED — Shannon's queue */}
      <Section title="Awaiting disbursement" sub={canDisburse(role, isSuperUser) ? "Pay these out and log the method." : "Shannon disburses these."}>
        {approved.length === 0 ? (
          <Empty>Nothing awaiting disbursement.</Empty>
        ) : (
          approved.map((d) => {
            const isActing = acting.has(`pay-${d.id}`);
            return (
              <div key={d.id} className="bg-white border border-slate-200 rounded-xl p-4 mb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">
                      {recipientName(d)}{" "}
                      <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                        ✓ Approved
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Approved {d.approved_at ? timeAgo(d.approved_at) : "—"}
                      {d.approval_notes ? ` · "${d.approval_notes}"` : ""}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-extrabold text-blue-700">{fmtUSD(Number(d.approved_amount ?? 0))}</div>
                  </div>
                </div>
                {canDisburse(role, isSuperUser) && (
                  <DisbursementInline
                    onMarkPaid={(method, ref) => markPaid(d, method, ref)}
                    disabled={isActing}
                  />
                )}
              </div>
            );
          })
        )}
      </Section>

      {/* OUTSTANDING BALANCES */}
      <Section title="Outstanding balances" sub="Taken off the person's next payout when Books pays it (Bill pay applies open draws first).">
        {balances.length === 0 ? (
          <Empty>No outstanding balances.</Empty>
        ) : (
          balances.map((b) => (
            <div key={b.key} className="bg-white border border-slate-200 rounded-xl p-4 mb-2 flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">
                  {b.name}{" "}
                  <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{b.role}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  {b.kind === "vendor" ? "Vendor draw — repaid via reduced crew bills" : "Repaid via reduced commission bills (FIFO)"}
                </div>
              </div>
              <div className="text-lg font-extrabold text-purple-700">{fmtUSD(b.balance)}</div>
            </div>
          ))
        )}
      </Section>

      {paidThisCycle.length > 0 && (
        <div className="text-[11px] text-slate-400">
          Paid this cycle (last 30 days): {paidThisCycle.length} draw{paidThisCycle.length === 1 ? "" : "s"} · {fmtUSD(paidThisCycle.reduce((s, d) => s + Number(d.approved_amount ?? 0), 0))}
        </div>
      )}
    </div>
  );
}

function DisbursementInline({ onMarkPaid, disabled }: { onMarkPaid: (method: string, ref: string) => void; disabled: boolean }) {
  const [method, setMethod] = useState("ach");
  const [ref, setRef] = useState("");
  return (
    <div className="mt-3 flex flex-wrap gap-2 items-center">
      <select value={method} onChange={(e) => setMethod(e.target.value)} className="text-xs border border-slate-300 rounded px-2 py-1">
        <option value="ach">ACH</option>
        <option value="check">Check</option>
        <option value="wire">Wire</option>
        <option value="cash">Cash</option>
      </select>
      <input
        type="text"
        placeholder="Reference (check #, ACH ref)"
        value={ref}
        onChange={(e) => setRef(e.target.value)}
        className="flex-1 min-w-[150px] text-xs border border-slate-300 rounded px-2 py-1"
      />
      <button
        onClick={() => onMarkPaid(method, ref)}
        disabled={disabled}
        className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-orange text-white hover:opacity-90 disabled:opacity-50"
      >
        {disabled ? "Saving…" : "Mark paid"}
      </button>
    </div>
  );
}

function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2">
        <h3 className="text-sm font-semibold text-bardin-ink">{title}</h3>
        {sub && <p className="text-xs text-slate-500">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="bg-white border border-dashed border-slate-200 rounded-xl p-6 text-sm text-slate-500 text-center">{children}</div>;
}

function SummaryCard({ label, value, sub, valueClass }: { label: string; value: number; sub?: string; valueClass?: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={"text-2xl font-extrabold mt-1 " + (valueClass ?? "text-bardin-ink")}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function timeAgo(ts: string): string {
  const ms = Date.now() - new Date(ts).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
